/**
 * 기준 밴드 밖 비율(순수 계산). backend `service/diagnostics/metric/bands.py::episode_deviation`
 * 의 정의를 그대로 따른다:
 * - 값은 에피소드 **mean** 그리드 점이 밴드 `[low, high]` 밖(strict 부등호)인 비율이다.
 * - 7D pose 는 위치 세 점과, p90 반경을 넘는 회전각 한 점으로 비교한다.
 * - 비유한 점은 분모에서 뺀다.
 *
 * 서버도 같은 값을 `outside_reference_fraction` 으로 준다(`/diagnostics/episodes`).
 * 그래도 그 값으로 대체하지 않는다. 화면은 채널·dim 별로도 비율을 그리고, 그 내역은 응답에 없다.
 * 서버 pooled 값과 화면 재계산 값을 함께 쓰면 합계와 내역이 달라진다. 그래서 한쪽에서만 계산한다.
 *
 * 임계값은 표시 언어와 함께 FE 가 소유한다. backend 는 판정하지 않는다. 성분 분위수와
 * 회전 반경은 비교 분모가 다르다. 그래서 이 수치는 결함 확률이 아니다.
 *
 * Action과 Observation은 같은 임계값(35% 주의, 45% 경고)을 쓴다.
 * 회전 반경을 포함한 분포에서 이 임계값이 적절한지는 별도 실측으로 확인해야 한다.
 */

export const DEVIATION_WARN_FRACTION = 0.35;
export const DEVIATION_ALERT_FRACTION = 0.45;

/** 이탈 심각도. ok=정상 / warn=주의 / alert=경고. */
export type DeviationSeverity = "ok" | "warn" | "alert";

/** 이탈률을 심각도 구간으로 바꾼다. 경계값을 포함한다(≥35% warn / ≥45% alert). */
export function deviationSeverity(fraction: number): DeviationSeverity {
  if (fraction >= DEVIATION_ALERT_FRACTION) {
    return "alert";
  }
  if (fraction >= DEVIATION_WARN_FRACTION) {
    return "warn";
  }
  return "ok";
}

/** 한 dim 의 이탈 집계. pooling 에 쓰려고 비율과 함께 분자·분모를 전달한다. */
export type DimDeviation = {
  outside: number;
  // 비교에 실제로 쓴 점의 수. isfinite(mean ∧ low ∧ high) 인 점만 센다.
  compared: number;
  // compared 가 0 이면 null 이다. 0% 와 "잴 수 없음"을 구분한다.
  fraction: number | null;
};

/** 한 dim 의 mean 그리드를 밴드와 비교한다. 경계 위(=low/high)는 안쪽이다. */
export function dimDeviation(
  mean: readonly number[],
  low: readonly number[],
  high: readonly number[],
): DimDeviation {
  const n = Math.min(mean.length, low.length, high.length);
  let outside = 0;
  let compared = 0;
  for (let t = 0; t < n; t += 1) {
    const m = mean[t];
    const lo = low[t];
    const hi = high[t];
    if (!Number.isFinite(m) || !Number.isFinite(lo) || !Number.isFinite(hi)) {
      continue;
    }
    compared += 1;
    if (m < lo || m > hi) {
      outside += 1;
    }
  }
  return { outside, compared, fraction: compared > 0 ? outside / compared : null };
}

/**
 * dim 들의 pooled 이탈률 = Σoutside / Σcompared. dim 별 fraction 의 평균이 아니다.
 * 비교점 수가 다른 dim 이 섞이면, 평균은 분모가 작은 dim 쪽으로 치우친다(backend 와 같은 정의).
 */
export function pooledDeviation(dims: readonly DimDeviation[]): number | null {
  let outside = 0;
  let compared = 0;
  for (const dim of dims) {
    outside += dim.outside;
    compared += dim.compared;
  }
  return compared > 0 ? outside / compared : null;
}

/**
 * 같은 위상의 두 wxyz 회전 사이 최단 각도(rad).
 * 상류 geodesic_angles 와 같은 정규화·부호 정렬·atan2 식을 쓴다. 그래서 작은 회전도 보존한다.
 * 영벡터·비유한 회전은 NaN 으로 둔다. 그래서 비교 분모에서 제외된다.
 */
export function rotationAngles(
  quaternion: readonly (readonly number[])[],
  reference: readonly (readonly number[])[],
): number[] {
  return (reference[0] ?? []).map((_, point) => {
    const left = unitQuaternion(quaternion.map((row) => row[point]));
    const right = unitQuaternion(reference.map((row) => row[point]));
    if (left === null || right === null) return Number.NaN;
    const dot = left.reduce((sum, value, dim) => sum + value * right[dim], 0);
    const sign = dot < 0 ? -1 : 1;
    let difference = 0;
    let total = 0;
    for (let dim = 0; dim < 4; dim += 1) {
      difference += (left[dim] - sign * right[dim]) ** 2;
      total += (left[dim] + sign * right[dim]) ** 2;
    }
    return 4 * Math.atan2(Math.sqrt(difference), Math.sqrt(total));
  });
}

function unitQuaternion(values: readonly number[]): number[] | null {
  if (values.length !== 4 || values.some((value) => !Number.isFinite(value))) return null;
  const norm = Math.sqrt(values.reduce((sum, value) => sum + value * value, 0));
  // hatchery-data ZERO_NORM_TOLERANCE 와 같은 회전 유효성 임계값이다.
  if (!Number.isFinite(norm) || norm < 1e-12) return null;
  return values.map((value) => value / norm);
}
